import React, { useState } from "react";
import { Button } from "../../design/button";
import {
  Dialog,
  DialogActions,
  DialogBody,
  DialogTitle,
} from "../../design/dialog";
import { Field, Fieldset, Label, ErrorMessage } from "../../design/fieldset";
import { Checkbox, CheckboxField, CheckboxGroup } from "../../design/checkbox";
import { useCase } from "../../contexts/CaseContext";
import { Subheading } from "../../design/heading";
import { Case } from "@visitproof/types";
import { Select } from "../../design/select";
import { httpsCallable } from "firebase/functions";
import { functions } from "../../firebase";
import { Text } from "../../design/text";
import {
  DescriptionDetails,
  DescriptionList,
  DescriptionTerm,
} from "../../design/description-list";
import { useParent } from "../../contexts/ParentContext";

interface Errors {
  petitionerId?: string;
  respondentId?: string;
}

interface Props {
  caseItem: Case;
  reportId?: string;
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
}

const CreateFL324PDialog: React.FC<Props> = ({
  caseItem,
  reportId,
  isOpen,
  setIsOpen,
}) => {
  const { selectedCase } = useCase();
  const { parents } = useParent();
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<Error | null>();

  const [petitionerId, setPetitionerId] = useState<string | null>(null);
  const [respondentId, setRespondentId] = useState<string | null>(null);
  const [copiesSentToAttorneys, setCopiesSentToAttorneys] =
    useState<boolean>(true);
  const [copiesSentToAttorneysOfChild, setCopiesSentToAttorneysOfChild] =
    useState<boolean>(false);
  // When on, the visit's event log is appended to the generated court report.
  // Only meaningful when creating from a report (which has a visit).
  const [includeEvents, setIncludeEvents] = useState<boolean>(false);
  const [fourA, setFourA] = useState<boolean>(true);
  const [fourB, setFourB] = useState<boolean>(true);
  const [fourC, setFourC] = useState<boolean>(true);
  const [fourD, setFourD] = useState<boolean>(true);
  const [fourE, setFourE] = useState<boolean>(true);
  const [fourF, setFourF] = useState<boolean>(true);
  const [fourG, setFourG] = useState<boolean>(true);
  const [fourH, setFourH] = useState<boolean>(true);
  const [fourI, setFourI] = useState<boolean>(true);
  const [fourJ, setFourJ] = useState<boolean>(true);
  const [fourK, setFourK] = useState<boolean>(true);
  const [fiveA, setFiveA] = useState<boolean>(true);
  const [fiveB, setFiveB] = useState<boolean>(true);
  const [errors, setErrors] = useState<Errors>({});

  const validateForm = () => {
    const newErrors: Errors = {};
    if (reportId && !petitionerId) {
      newErrors.petitionerId = "Petitioner is required";
    }
    if (reportId && !respondentId) {
      newErrors.respondentId = "Respondent is required";
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }
    setIsSaving(true);
    try {
      const { data } = (await httpsCallable(
        functions,
        "createFL324P",
      )({
        typeOfSubmission: reportId ? "2b" : "2a",
        reportId,
        caseId: caseItem.id,
        respondentId,
        petitionerId,
        includeEvents,
        copiesSentToAttorneys,
        copiesSentToAttorneysOfChild,
        fourA,
        fourB,
        fourC,
        fourD,
        fourE,
        fourF,
        fourG,
        fourH,
        fourI,
        fourJ,
        fourK,
        fiveA,
        fiveB,
      })) as { data: { downloadUrl: string } };
      window.open(data.downloadUrl, "_blank");
      clearForm();
    } catch (error) {
      setError(error as Error);
    } finally {
      setIsSaving(false);
    }
  };

  const clearForm = () => {
    setIsOpen(false);
  };

  const filteredParents = parents.filter(
    (parent) => parent.caseId === caseItem.id,
  );

  return (
    <>
      <Dialog open={isOpen} onClose={setIsOpen}>
        <DialogTitle>Create FL-324(P)</DialogTitle>
        <DialogBody>
          {error && <ErrorMessage>{error.message}</ErrorMessage>}
          <Subheading>Court Information</Subheading>
          <DescriptionList>
            {selectedCase?.courtAddress?.county && (
              <>
                <DescriptionTerm>County</DescriptionTerm>
                <DescriptionDetails>
                  {selectedCase?.courtAddress?.county}
                </DescriptionDetails>
              </>
            )}
            {selectedCase?.courtAddress?.streetAddress && (
              <>
                <DescriptionTerm>Street Address</DescriptionTerm>
                <DescriptionDetails>
                  {selectedCase?.courtAddress?.streetAddress}
                </DescriptionDetails>
              </>
            )}
            {selectedCase?.courtAddress?.mailingAddress && (
              <>
                <DescriptionTerm>Mailing Address</DescriptionTerm>
                <DescriptionDetails>
                  {selectedCase?.courtAddress?.mailingAddress}
                </DescriptionDetails>
              </>
            )}
            {selectedCase?.courtAddress?.city && (
              <>
                <DescriptionTerm>City</DescriptionTerm>
                <DescriptionDetails>
                  {selectedCase?.courtAddress?.city}
                </DescriptionDetails>
              </>
            )}
            {selectedCase?.courtAddress?.zipCode && (
              <>
                <DescriptionTerm>Zip Code</DescriptionTerm>
                <DescriptionDetails>
                  {selectedCase?.courtAddress?.zipCode}
                </DescriptionDetails>
              </>
            )}
            {selectedCase?.courtAddress?.branchName && (
              <>
                <DescriptionTerm>Branch Name</DescriptionTerm>
                <DescriptionDetails>
                  {selectedCase?.courtAddress?.branchName}
                </DescriptionDetails>
              </>
            )}
          </DescriptionList>
          <Fieldset>
            <Field>
              <Label htmlFor="petitioner">Petioner</Label>
              <Select
                id="petitioner"
                value={petitionerId || ""}
                onChange={(e) => setPetitionerId(e.target.value)}
              >
                <option value="">Select a parent</option>
                {filteredParents.map((parent) => (
                  <option key={parent.id} value={parent.id}>
                    {parent.firstName} {parent.lastName}
                  </option>
                ))}
              </Select>
              {errors.petitionerId && (
                <ErrorMessage>{errors.petitionerId}</ErrorMessage>
              )}
            </Field>
            <Field>
              <Label htmlFor="respondent">Respondent</Label>
              <Select
                id="respondent"
                value={respondentId || ""}
                onChange={(e) => setRespondentId(e.target.value)}
              >
                <option value="">Select Respondent</option>
                {filteredParents.map((parent) => (
                  <option key={parent.id} value={parent.id}>
                    {parent.firstName} {parent.lastName}
                  </option>
                ))}
              </Select>
              {errors.respondentId && (
                <ErrorMessage>{errors.respondentId}</ErrorMessage>
              )}
            </Field>

            {reportId && (
              <>
                <CheckboxGroup>
                  <Subheading>Include Event Log</Subheading>
                  <CheckboxField>
                    <Checkbox
                      name="includeEvents"
                      checked={includeEvents}
                      onChange={() => setIncludeEvents(!includeEvents)}
                    />
                    <Label>
                      <strong>Attach visit events:</strong> append the visit's
                      timestamped observations, interactions, and incidents to
                      the court report.
                    </Label>
                  </CheckboxField>
                </CheckboxGroup>
                <CheckboxGroup>
                  <Subheading>Type of Submission</Subheading>
                  <CheckboxField>
                    <Checkbox
                      name="copiesSentToAttorneys"
                      checked={copiesSentToAttorneys}
                      onChange={(e) =>
                        setCopiesSentToAttorneys(!copiesSentToAttorneys)
                      }
                    />
                    <Label>
                      <strong>Copies Sent to Attorneys:</strong> Copies of this
                      report were sent to the attorneys of record for the
                      parties.
                    </Label>
                  </CheckboxField>
                  <CheckboxField>
                    <Checkbox
                      name="copiesSentToAttorneysOfChild"
                      checked={copiesSentToAttorneysOfChild}
                      onChange={(e) =>
                        setCopiesSentToAttorneysOfChild(
                          !copiesSentToAttorneysOfChild,
                        )
                      }
                    />
                    <Label>
                      <strong>Copies Sent to Attorneys of Child:</strong> Copies
                      of this report were sent to the attorneys of record for
                      the child(ren).
                    </Label>
                  </CheckboxField>
                </CheckboxGroup>
              </>
            )}

            <CheckboxGroup>
              <Subheading>Qualifications</Subheading>
              <Text>
                I meet the qualifications listed in Family Code section 3200.5
                for this position as follows (check all that apply):
              </Text>
              <CheckboxField>
                <Checkbox
                  name="4A"
                  checked={fourA}
                  onChange={(e) => setFourA(!fourA)}
                />
                <Label>
                  <strong>4A:</strong> I am 21 years of age
                </Label>
              </CheckboxField>
              <CheckboxField>
                <Checkbox
                  name="4B"
                  checked={fourB}
                  onChange={(e) => setFourB(!fourB)}
                />
                <Label>
                  <strong>4B:</strong> I have no record of a conviction for
                  driving under the influence (DUI) within the last five years.
                </Label>
              </CheckboxField>
              <CheckboxField>
                <Checkbox
                  name="4C"
                  checked={fourC}
                  onChange={(e) => setFourC(!fourC)}
                />
                <Label>
                  <strong>4C:</strong> I have not been on probation or parole
                  for the last 10 years.
                </Label>
              </CheckboxField>
              <CheckboxField>
                <Checkbox
                  name="4D"
                  checked={fourD}
                  onChange={(e) => setFourD(!fourD)}
                />
                <Label>
                  <strong>4D:</strong> I have no record of a conviction for
                  child molestation, child abuse, or other crimes against a
                  person.
                </Label>
              </CheckboxField>
              <CheckboxField>
                <Checkbox
                  name="4E"
                  checked={fourE}
                  onChange={(e) => setFourE(!fourE)}
                />
                <Label>
                  <strong>4E:</strong> I have proof of automobile insurance for
                  transporting the child.
                </Label>
              </CheckboxField>
              <CheckboxField>
                <Checkbox
                  name="4F"
                  checked={fourF}
                  onChange={(e) => setFourF(!fourF)}
                />
                <Label>
                  <strong>4F:</strong> I have had no civil, criminal, or
                  juvenile restraining orders within the last 10 years.
                </Label>
              </CheckboxField>
              <CheckboxField>
                <Checkbox
                  name="4G"
                  checked={fourG}
                  onChange={(e) => setFourG(!fourG)}
                />
                <Label>
                  <strong>4G:</strong> There is no current or past court order
                  in which I am the person being supervised.
                </Label>
              </CheckboxField>
              <CheckboxField>
                <Checkbox
                  name="4H"
                  checked={fourH}
                  onChange={(e) => setFourH(!fourH)}
                />
                <Label>
                  <strong>4H:</strong> I agree to speak the language of the
                  party being supervised and of the child, or I will provide a
                  neutral interpreter over the age of 18 years who is able to do
                  so.
                </Label>
              </CheckboxField>
              <CheckboxField>
                <Checkbox
                  name="4I"
                  checked={fourI}
                  onChange={(e) => setFourI(!fourI)}
                />
                <Label>
                  <strong>4I:</strong> I agree to adhere to and enforce the
                  court order regarding supervised visitation.
                </Label>
              </CheckboxField>
              <CheckboxField>
                <Checkbox
                  name="4J"
                  checked={fourJ}
                  onChange={(e) => setFourJ(!fourJ)}
                />
                <Label>
                  <strong>4J:</strong> I completed a Live Scan criminal
                  background check before providing services.
                </Label>
              </CheckboxField>
              <CheckboxField>
                <Checkbox
                  name="4K"
                  checked={fourK}
                  onChange={(e) => setFourK(!fourK)}
                />
                <Label>
                  <strong>4K:</strong> I am registered as a TrustLine provider.
                </Label>
              </CheckboxField>
              <Subheading>Training</Subheading>
              <CheckboxField>
                <Checkbox
                  name="5A"
                  checked={fiveA}
                  onChange={(e) => setFiveA(!fiveA)}
                />
                <Label>
                  <strong>5A:</strong> I completed 24 hours of training,
                  including at least 12 hours of classroom instruction in all
                  required subjects.
                </Label>
              </CheckboxField>
              <CheckboxField>
                <Checkbox
                  name="5B"
                  checked={fiveB}
                  onChange={(e) => setFiveB(!fiveB)}
                />
                <Label>
                  <strong>5B:</strong> I completed the California Department of
                  Social Services' online training course required for mandated
                  reporters.
                </Label>
              </CheckboxField>
            </CheckboxGroup>
          </Fieldset>
        </DialogBody>
        <DialogBody>
          {errors.petitionerId && (
            <ErrorMessage>{errors.petitionerId}</ErrorMessage>
          )}
          {errors.respondentId && (
            <ErrorMessage>{errors.respondentId}</ErrorMessage>
          )}
        </DialogBody>
        <DialogActions>
          <Button plain onClick={clearForm}>
            Cancel
          </Button>
          <Button disabled={isSaving} onClick={handleSubmit}>
            {isSaving ? "Exporting..." : "Export"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default CreateFL324PDialog;
